import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import imageCompression from 'browser-image-compression';
import JSZip from 'jszip';
import LogoTransparent from '../../images/official_logo_transparency.png';

const DownloadImages: React.FC<{ images: string[] }> = ({ images }) => {
    const [uid, setUid] = useState<string>('');
    const [logoSize, setLogoSize] = useState<number>(25);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [processingProgress, setProcessingProgress] = useState<number>(0);
    const [downloadFormat, setDownloadFormat] = useState<'webp' | 'jpeg'>('webp');

    useEffect(() => {
        const currentUrl = window.location.href;
        const uidMatch = currentUrl.match(/details-announce-([a-f0-9]+)/);
        if (uidMatch) {
            setUid(uidMatch[1]);
        }
    }, []);

    const applyLogoToImage = async (imageBlob: Blob): Promise<Blob> => {
        return new Promise<Blob>((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const img = new Image();
            img.src = URL.createObjectURL(imageBlob);
            
            img.onload = () => {
                const ctx = canvas.getContext('2d');
                if (!ctx) return;
                
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);

                const logo = new Image();
                logo.src = LogoTransparent;
                
                logo.onload = () => {
                    const logoWidth = (img.width * logoSize) / 100;
                    const logoHeight = (logo.height * logoWidth) / logo.width;
                    const x = (img.width - logoWidth) / 2;
                    const y = (img.height - logoHeight) / 2;
                    
                    ctx.drawImage(logo, x, y, logoWidth, logoHeight);
                    
                    canvas.toBlob((blob) => {
                        if (blob) {
                            resolve(blob);
                        }
                    }, 'image/jpeg', 0.9);
                };
            };
        });
    };

    const getFileExtension = () => downloadFormat === 'jpeg' ? 'jpg' : 'webp';

    const downloadImage = async (imgUrl: string, withLogo: boolean = false) => {
        try {
            const response = await axios.get(imgUrl, { responseType: 'blob' });
            let blob: Blob = response.data;
            
            if (withLogo) {
                blob = await applyLogoToImage(blob);
            }
            
            const originalExtension = imgUrl.split('.').pop()?.toLowerCase() || 'jpg';
            const filename = `${uid}_${withLogo ? 'logo_' : ''}${imgUrl.split('/').pop()?.replace(/\.[^/.]+$/, '')}.${originalExtension}`;
            saveAs(blob, filename);
        } catch (error) {
            console.error('Error downloading image:', error);
        }
    };

    const downloadCompressedImage = async (imgUrl: string, withLogo: boolean = false) => {
        try {
            const response = await axios.get(imgUrl, { responseType: 'blob' });
            let blob: Blob = response.data;
            
            if (withLogo) {
                blob = await applyLogoToImage(blob);
            }
            
            const originalFilename = imgUrl.split('/').pop() || `image_${Date.now()}`;
            const file = new File([blob], originalFilename, { type: blob.type || 'image/jpeg' });
            
            const options = {
                maxSizeMB: 0.1,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                fileType: `image/${downloadFormat}`
            };
            
            const compressedBlob = await imageCompression(file, options);
            const filename = `${uid}-${withLogo ? 'logo_' : ''}compr-${originalFilename.replace(/\.[^/.]+$/, '')}.${getFileExtension()}`;
            saveAs(compressedBlob, filename);
        } catch (error) {
            console.error('Error compressing and downloading image:', error);
        }
    };

    const downloadAllImages = (withLogo: boolean = false) => {
        images.forEach(imgUrl => downloadImage(imgUrl, withLogo));
    };

    const downloadAndCompressAllImages = (withLogo: boolean = false) => {
        images.forEach(imgUrl => downloadCompressedImage(imgUrl, withLogo));
    };

    const downloadAllImagesAsZip = async (withLogo: boolean = false) => {
        setIsProcessing(true);
        setProcessingProgress(0);
        const zip = new JSZip();
        
        for (let i = 0; i < images.length; i++) {
            const imgUrl = images[i];
            try {
                const response = await axios.get(imgUrl, { responseType: 'blob' });
                let blob: Blob = response.data;
                
                if (withLogo) {
                    blob = await applyLogoToImage(blob);
                }
                
                const originalExtension = imgUrl.split('.').pop()?.toLowerCase() || 'jpg';
                const filename = `${uid}_${withLogo ? 'logo_' : ''}${imgUrl.split('/').pop()?.replace(/\.[^/.]+$/, '')}.${originalExtension}`;
                zip.file(filename, blob);
                
                setProcessingProgress(((i + 1) / images.length) * 100);
            } catch (error) {
                console.error('Error downloading image:', error);
            }
        }
        
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, `${uid}_${withLogo ? 'logo_' : ''}images.zip`);
        setIsProcessing(false);
    };

    const downloadAndCompressAllImagesAsZip = async (withLogo: boolean = false) => {
        setIsProcessing(true);
        setProcessingProgress(0);
        const zip = new JSZip();
        
        for (let i = 0; i < images.length; i++) {
            const imgUrl = images[i];
            try {
                const response = await axios.get(imgUrl, { responseType: 'blob' });
                let blob: Blob = response.data;
                
                if (withLogo) {
                    blob = await applyLogoToImage(blob);
                }
                
                const originalFilename = imgUrl.split('/').pop() || `image_${i}`;
                const file = new File([blob], originalFilename, { type: blob.type || 'image/jpeg' });
                
                const options = {
                    maxSizeMB: 0.1,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                    fileType: `image/${downloadFormat}`
                };
                
                const compressedBlob = await imageCompression(file, options);
                const filename = `${uid}-${withLogo ? 'logo_' : ''}compr-${originalFilename.replace(/\.[^/.]+$/, '')}.${getFileExtension()}`;
                zip.file(filename, compressedBlob);
                
                setProcessingProgress(((i + 1) / images.length) * 100);
            } catch (error) {
                console.error(`Error processing image ${imgUrl}:`, error);
            }
        }
        
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, `${uid}_${withLogo ? 'logo_' : ''}compressed_images.zip`);
        setIsProcessing(false);
    };

    return (
        <div>
            <div style={{ marginBottom: '20px' }}>
                <label>
                    Logo Size (% of image width): 
                    <select 
                        value={logoSize} 
                        onChange={(e) => setLogoSize(Number(e.target.value))}
                        style={{ marginLeft: '10px' }}
                    >
                        <option value={10}>10%</option>
                        <option value={15}>15%</option>
                        <option value={20}>20%</option>
                        <option value={25}>25%</option>
                        <option value={30}>30%</option>
                        <option value={50}>50%</option>
                    </select>
                </label>

                <label style={{ marginLeft: '20px' }}>
                    Compressed Format:
                    <select 
                        value={downloadFormat}
                        onChange={(e) => setDownloadFormat(e.target.value as 'webp' | 'jpeg')}
                        style={{ marginLeft: '10px' }}
                    >
                        <option value="webp">WebP</option>
                        <option value="jpeg">JPEG</option>
                    </select>
                </label>
            </div>
            
            {isProcessing && (
                <div style={{ margin: '10px 0', padding: '10px', background: '#f0f0f0' }}>
                    <div>Processing... {Math.round(processingProgress)}%</div>
                    <progress value={processingProgress} max="100" style={{ width: '100%' }} />
                </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px' }}>
                <div>
                    <h3>Original Images</h3>
                    <button onClick={() => downloadAllImages()} disabled={!images.length || isProcessing}>
                        Download All Images
                    </button>
                    <button onClick={() => downloadAllImages(true)} disabled={!images.length || isProcessing}>
                        Download All with Logo
                    </button>
                    <button onClick={() => downloadAllImagesAsZip()} disabled={!images.length || isProcessing}>
                        Download All as ZIP
                    </button>
                    <button onClick={() => downloadAllImagesAsZip(true)} disabled={!images.length || isProcessing}>
                        Download All with Logo as ZIP
                    </button>
                </div>
                
                <div>
                    <h3>Compressed Images ({downloadFormat.toUpperCase()})</h3>
                    <button onClick={() => downloadAndCompressAllImages()} disabled={!images.length || isProcessing}>
                        Download & Compress All
                    </button>
                    <button onClick={() => downloadAndCompressAllImages(true)} disabled={!images.length || isProcessing}>
                        Download & Compress with Logo
                    </button>
                    <button onClick={() => downloadAndCompressAllImagesAsZip()} disabled={!images.length || isProcessing}>
                        Download Compressed as ZIP
                    </button>
                    <button onClick={() => downloadAndCompressAllImagesAsZip(true)} disabled={!images.length || isProcessing}>
                        Download Compressed with Logo as ZIP
                    </button>
                </div>
            </div>
            
            <div style={{ marginTop: '20px' }}>
                {images.map((imgUrl, index) => (
                    <img 
                        key={index} 
                        src={imgUrl} 
                        alt={`img-${index}`} 
                        style={{ maxWidth: '100px', margin: '10px' }} 
                    />
                ))}
            </div>
        </div>
    );
};

export default DownloadImages;